import { useApplicantAPI } from "@certn/api";
import { components } from "@certn/api/generated-schemas/applicant-portal";
import {
  ActionList,
  Divider,
  FilterChip,
  FilterChipGroup,
  Text,
} from "@certn/cascadia-react";
import { Skeleton } from "@certn/cascadia-react/patterns/skeleton/Skeleton";
import { css } from "@emotion/css";
import { Fragment, useState } from "react";
import { Trans, useTranslation } from "react-i18next";

import { HistoryCard } from "./history-card";
import { compareCompleteCases } from "./utils";
import { EmptyState } from "../../components/empty-state/EmptyState";

type Case = components["schemas"]["CompleteCaseList"];

export function HistoryCards() {
  const { t } = useTranslation();
  const [activeButton, setActiveButton] = useState<
    "all" | "processing" | "complete"
  >("all");
  const { data: cases, isLoading } = useApplicantAPI(
    "/api/internal/applicant/case/complete/"
  );

  const inProgressStatuses = [
    "APPLICANT_SUBMITTED",
    "IN_DISPUTE",
    "IN_PROGRESS",
    "PENDING_FULFILLMENT",
    "CLIENT_ACTION_REQUIRED",
    "APPLICANT_ACTION_REQUIRED",
    "IN_QUALITY_REVIEW",
    "PENDING_QUALITY_REVIEW",
  ];
  const completedStatuses = [
    "APPLICANT_DECLINED",
    "COMPLETE",
    "CANCELLED",
    "APPLICANT_EXPIRED",
    "INVITE_UNDELIVERABLE",
  ];

  const allCases = (cases ?? []).sort(compareCompleteCases);
  const processingCases = allCases.filter(
    ({ archived_on, overall_status }) =>
      inProgressStatuses.includes(overall_status) && !archived_on
  );
  const completeCases = allCases.filter(
    ({ archived_on, overall_status }) =>
      completedStatuses.includes(overall_status) && !archived_on
  );

  const renderCards = (cases: Case[]) => (
    <ActionList>
      {cases?.map((caseInstance) => {
        return (
          <Fragment key={caseInstance.short_id}>
            <HistoryCard caseInstance={caseInstance} />
            <Divider />
          </Fragment>
        );
      })}
    </ActionList>
  );

  const EmptyProcessingBackgroundChecksCard = () => (
    <EmptyState
      illustration="check-processing"
      description={<Trans>You have no processing background checks.</Trans>}
    />
  );

  const EmptyCompletedBackgroundChecksCard = () => (
    <EmptyState
      illustration="check-success"
      description={<Trans>You have no completed background checks.</Trans>}
    />
  );

  return (
    <section aria-label="history">
      <div className={styles.headingContainer}>
        <Text as="h2" style="global/heading-large">
          <Trans>History</Trans>
        </Text>
        <FilterChipGroup>
          <FilterChip
            onChange={() => {
              setActiveButton("all");
            }}
            testID="dashboard-history-all-button"
            isSelected={activeButton === "all"}
          >
            {t("All")}
          </FilterChip>
          <FilterChip
            onChange={() => {
              setActiveButton("processing");
            }}
            testID="dashboard-history-processing-button"
            isSelected={activeButton === "processing"}
          >
            {t("In progress")}
          </FilterChip>
          <FilterChip
            onChange={() => {
              setActiveButton("complete");
            }}
            testID="dashboard-history-complete-button"
            isSelected={activeButton === "complete"}
          >
            {t("Complete")}
          </FilterChip>
        </FilterChipGroup>
      </div>
      {isLoading ? (
        <div
          data-testid="history-cards-skeleton"
          className={css({
            display: "flex",
            flexDirection: "column",
            gap: "23px",
          })}
        >
          <Skeleton height="24px" width="254px" />
          <div
            className={css({
              gap: "10px",
            })}
          >
            <Skeleton height="10px" width="205px" />
            <Skeleton height="10px" width="157px" />
            <Skeleton height="10px" width="157px" />
          </div>
        </div>
      ) : (
        <>
          {activeButton === "processing" &&
            (processingCases && processingCases.length > 0 ? (
              renderCards(processingCases)
            ) : (
              <EmptyProcessingBackgroundChecksCard />
            ))}
          {activeButton === "complete" &&
            (completeCases && completeCases.length > 0 ? (
              renderCards(completeCases)
            ) : (
              <EmptyCompletedBackgroundChecksCard />
            ))}
          {activeButton === "all" &&
            (allCases && allCases.length > 0 ? (
              renderCards(allCases)
            ) : (
              <EmptyCompletedBackgroundChecksCard />
            ))}
        </>
      )}
    </section>
  );
}

const styles = {
  headingContainer: css({
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "var(--space-4)",
  }),
};
